const T = require('shared/i18n/translator'),
    getCookie = require('shared/helpers/getCookie'),
    S = require('underscore.string');
// Getting the language cookie value here, in effect, caches the language setting for the function exported by this module,
// which speeds up its performance. This helped improve wellbore grid template performance a bit, but should also provide some
// benefit for the site as a whole. This should be safe to do, because language changes always necessitate a full page reload anyway.
const lang = getCookie('language') || 'en';

// Keys: The key used to lookup a translated word, phrase or translation message function.
// Type: String used to inform this module what type of text to form and return.  The switch
// statement below with its accompanying functions illustrates the various types.
// Words: Raw word values that will be inserted, untranslated, into the final output.
// Raw: After translation lookups and message forming, text is returned with no other formatting applied.
module.exports = function(keys, type = null, words = null, raw = false) {
    let result = null;
    let vals = [];
    // Make keys an array if it isn't for the sake of translater.
    if (!Array.isArray(keys)) {
        keys = [keys];
    }
    vals = T(keys);
    // If no translations are found, we just use the keys themselves.
    if (_.every(vals, (val) => val === '')) {
        vals = keys;
    }
    switch (type) {
        case 'add':
            result = add(vals[0]);
            break;
        case 'edit':
            result = edit(vals[0], words);
            break;
        case 'search':
            result = search(vals, raw);
            break;
        case 'select':
            result = select(vals[0]);
            break;
        case 'template':
            result = template(vals[0], words);
            break;
        default:
            result = basic(vals, raw);
            break;
    }
    return result;
};

// Basic is for simple key lookups with no real formatting.
// Primary use is formatting all one/few-word button and labels.
// Should also prove to be the most common.
// Can also be used to translate and basic format an array of terms for other uses.
function basic(vals, raw) {
    let result;
    if (vals.length > 1) {
        result = vals.map((val) => _basic(val, raw));
    }
    else {
        result = _basic(vals[0], raw);
    }
    return result;
}

// Split out the main logic of basic to be privately reusable.
function _basic(txt, raw) {
    let result;
    if (lang === 'en' && !raw) {
        result = S(txt).titleize().value();
    }
    else if (!raw) {
        result = capitalize_es(txt);
    }
    else {
        result = txt;
    }
    return result;
}

// Add is for all labels and buttons that describe an add action or page.
function add(txt) {
    let result;
    let verb = T('add')[0];
    result = `${verb} ${txt}`;
    if (lang === 'en') {
        result = S(result).titleize().value();
    }
    else {
        result = capitalize_es(result);
    }
    return result;
}

// Edit is for all labels and buttons that describe an edit action or page.
function edit(txt, words) {
    let result;
    let verb = T('edit')[0];
    result = `${verb} ${txt}`;
    if (lang === 'en') {
        result = S(result).titleize().value();
    }
    else {
        result = capitalize_es(result);
    }
    if (words) {
        result = `${result} "${words}"`;
    }
    return result;
}

// Search is for all search placeholders.
// Might extend this later to also cover "Filter by" placeholders.
function search(vals, raw) {
    let result;
    let verb = T('search-by')[0];
    if (!raw) {
        result = `${verb} ${vals.join(', ')}`;
        result = S(result).humanize().value();
    }
    else {
        verb = S(verb).humanize().value();
        result = `${verb} ${vals.join(', ')}`;
    }
    return result;
}

// Select is for all select2 placeholders.
function select(txt) {
    let result;
    let verb = T('select')[0];
    result = `${verb} ${txt}`;
    result = `- ${S(result).humanize().value()} -`;
    return result;
}

// Template is for long or more complex messages that require template strings.
function template(fn, words) {
    if (!Array.isArray(words)) {
        words = [words];
    }
    // If the key passed in doesn't refer to a function then there's nothing to do.
    if (typeof fn !== 'function') {
        return '';
    }
    let result;
    result = fn(words);
    return result;
}
// -----------------------------------------------------------------------------

// Helper functions.

// Helper for the string.js Capitalize function, which can't handle Spanish's
// strings that have leading punctuation, or Spanish special characters.
function capitalize_es(txt) {
    let result;
    // This prevents the substr() call in the following else-if from failing if txt is a number.
    if (typeof(txt) === 'number') {
        result = txt;
    }
    // We need to add the special Spanish characters to the regex, to ensure that only strings with leading punctuation trip this condition.
    else if (!/^[a-zA-Záéíóúüñ]/.test(txt.toLowerCase())) {
        let first = txt.substr(0, 1);
        let rest = txt.substr(1);
        result = `${first}${S(rest).humanize().value()}`
    }
    else {
        result = S(txt).humanize().value();
    }
    return result;
}
